import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, Input } from '@ai-tutor/ui';
import {
  MAX_TEXT_CHARS,
  MAX_TEXT_TITLE_CHARS,
  type StagedSource,
} from '../types';
import type { TabFocusHandle } from './UploadFileTab';

interface PasteTextTabProps {
  onAdd: (source: StagedSource) => void | Promise<void>;
}

const HELPER =
  'Paste any plain text - notes, transcripts, articles. Up to 500,000 characters.';
const WARN_THRESHOLD = Math.floor(MAX_TEXT_CHARS * 0.9);

// Shared className snippet that suppresses the dark default focus ring on
// `Input` and matches the pattern used elsewhere in the app (e.g.
// FolderFormModal, CompactSearchBar).
const INPUT_NO_RING =
  'border-border bg-card shadow-none ring-0 outline-none focus:border-brand-400 dark:focus:border-primary focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:focus:border-primary';

export const PasteTextTab = forwardRef<TabFocusHandle, PasteTextTabProps>(
  function PasteTextTab({ onAdd }, ref) {
    const titleRef = useRef<HTMLInputElement>(null);
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isBusy, setIsBusy] = useState(false);

    useImperativeHandle(ref, () => ({
      focus: () => titleRef.current?.focus(),
    }));

    const titleTrimmed = title.trim();
    const textLen = text.length;
    const isValid = titleTrimmed.length > 0 && textLen > 0;
    const overWarn = textLen >= WARN_THRESHOLD;

    const handleTextChange = (next: string) => {
      // Hard truncate - backend rejects over-limit, no point keeping it.
      setText(
        next.length > MAX_TEXT_CHARS ? next.slice(0, MAX_TEXT_CHARS) : next
      );
    };

    const handleSubmit = async () => {
      if (!isValid) return;
      setSubmitError(null);
      setIsBusy(true);
      try {
        await onAdd({
          kind: 'text',
          text,
          name: titleTrimmed,
        });
        setTitle('');
        setText('');
      } catch (err: any) {
        setSubmitError(err?.message || 'Failed to add text');
      } finally {
        setIsBusy(false);
      }
    };

    return (
      // Sticky-footer layout: form scrolls if it overflows, the Add button
      // stays pinned to the bottom so it's never lost behind the modal edge.
      <div className='flex h-full flex-col'>
        <div className='flex-1 overflow-y-auto px-4 pt-4 sm:px-6 sm:pt-6'>
          <div className='flex flex-col gap-4 pb-4'>
            <p className='text-xs text-muted-foreground'>{HELPER}</p>

            <div>
              <label
                htmlFor='attach-text-title'
                className='mb-1 block text-xs font-medium text-muted-foreground'
              >
                Title
              </label>
              <Input
                id='attach-text-title'
                ref={titleRef}
                placeholder='e.g. Lecture 4 - Closures'
                value={title}
                onChange={e =>
                  setTitle(e.target.value.slice(0, MAX_TEXT_TITLE_CHARS))
                }
                disabled={isBusy}
                // 44px on a phone, like every auth field; 36px from `sm` up.
                className={`h-11 sm:h-9 ${INPUT_NO_RING}`}
              />
            </div>

            <div>
              <label
                htmlFor='attach-text-body'
                className='mb-1 block text-xs font-medium text-muted-foreground'
              >
                Text
              </label>
              <textarea
                id='attach-text-body'
                rows={6}
                placeholder='Paste your notes or transcript here…'
                value={text}
                onChange={e => handleTextChange(e.target.value)}
                disabled={isBusy}
                className='w-full resize-y rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-brand-400 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:focus:border-primary'
              />
              <div
                className={`mt-1 text-right text-xs ${
                  overWarn
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-muted-foreground'
                }`}
              >
                {textLen.toLocaleString()} / {MAX_TEXT_CHARS.toLocaleString()}
              </div>
            </div>

            {submitError && (
              <p className='text-xs text-red-500'>{submitError}</p>
            )}

            <div className='pt-1 sm:flex sm:justify-end'>
              <Button
                type='button'
                size='sm'
                onClick={handleSubmit}
                disabled={!isValid || isBusy}
                className='h-11 w-full min-w-[120px] px-8 sm:h-8 sm:w-auto'
              >
                {isBusy ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Adding…
                  </>
                ) : (
                  'Add text'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
